import { useState } from 'react'
import { BRAND_NAME, BRAND_TAGLINE } from '../shared/brand'
import { useAuth } from '../shared/hooks/useAuth'
import { apiClient } from '../shared/api/client'

export function Login() {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'phone' | 'code'>('phone')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { initialize } = useAuth()

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      // Send phone number to backend
      await apiClient.post('/api/auth/request-code', { phone })
      setStep('code')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send code'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await apiClient.post<{ token: string }>(
        '/api/auth/verify-code',
        { phone, code }
      )

      // Save token
      localStorage.setItem('maximur_auth_token', response.token)
      apiClient.setToken(response.token)

      // Re-initialize auth
      await initialize()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Invalid code'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-accent to-pink-500 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{BRAND_NAME}</h1>
          <p className="text-xl text-white/80">🐾 {BRAND_TAGLINE}</p>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 border border-white/10 dark:border-gray-700">
          {step === 'phone' ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Вход по номеру</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Номер телефона
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+7 999 123-45-67"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                />
              </div>

              {error && <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>}

              <button
                type="submit"
                disabled={isLoading || !phone}
                className="w-full bg-accent text-white py-3 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {isLoading ? 'Отправляем...' : 'Получить код'}
              </button>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Или откройте приложение в боте MAX
              </p>
            </form>
          ) : (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Введите код</h2>

              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Код отправлен на номер <br /> <strong className="text-gray-900 dark:text-white">{phone}</strong>
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Код подтверждения
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-center text-2xl tracking-widest bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                />
              </div>

              {error && <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>}

              <button
                type="submit"
                disabled={isLoading || code.length !== 6}
                className="w-full bg-accent text-white py-3 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {isLoading ? 'Проверяем...' : 'Войти'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('phone')
                  setCode('')
                  setError('')
                }}
                className="w-full text-accent text-sm hover:underline"
              >
                Изменить номер
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
